import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import DashboardHelpInfoTab from './Dashboard-HelpInfo-Tab';
import DashboardExportTab from './Dashboard-Export-Tab';
import SwitchProducts from './SwitchProducts'; // Reintroduce SwitchProducts component
import envConfig from '../envConfig';
import RiverMap from './RiverMap';
import { useSelectedProduct } from '../contexts/SelectedProductContext';

function Dashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedProduct, setSelectedProduct, isFromUI, setIsFromUI } = useSelectedProduct();

    const [selectedTab, setSelectedTab] = useState('cf');
    const [isOpen, setIsOpen] = useState(true);
    const [activeTab, setActiveTab] = useState(1);
    const [currentTab, setCurrentTab] = useState('layer');
    const [show, setShow] = useState(false); // State for SwitchProducts modal
    const [selectedBasinId, setSelectedBasinIds] = useState('');
    const [isTemporal, setIsTemporal] = useState(false);
    const [height, setHeight] = useState(window.innerHeight);
    const [basins, setBasins] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(0);
    const [lookUpLoading, setLookUpLoading] = useState(0);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [selectedBasinMap, setSelectedBasinMap] = useState(null);
    const [mapGeojsonLegend, setMapGeojsonLegend] = useState(null);
    const [selectedYear, setselectedYear] = useState(1990);
    const [exceededDataView, setExceededDataView] = useState('flow');
    const [stationDataType, setStationDataType] = useState('forecast');
    const [dayIndex, setDayIndex] = useState(0);

    // Sync product selection with URL
    useEffect(() => {
        const path = location.pathname;
        const productMatch = path.match(/^\/(product[1-4])\/dashboard$/);
        const newProduct = productMatch ? productMatch[1] : 'product4';

        if (newProduct !== selectedProduct && !isFromUI) {
            console.log(`URL Sync: Setting product to ${newProduct} from URL ${path}`);
            setSelectedProduct(newProduct);
            setActiveTab(1);
            setCurrentTab('layer');
        }
    }, [location.pathname, selectedProduct, setSelectedProduct, isFromUI, setIsFromUI]);

    // Handle product selection
    const handleProductSelect = (product) => {
        console.log(`Dropdown: Changing product to ${product}`);
        setIsFromUI(true);
        setSelectedProduct(product);
        setActiveTab(1);
        setCurrentTab('layer');
        navigate(`/${product}/dashboard`, { replace: false });
        setTimeout(() => setIsFromUI(false), 0);
    };

    const onTabSelect = (tab) => {
        if (!lookUpLoading && !loading) {
            setSelectedTab(tab);
        }
    };

    const toggleSidenav = () => {
        setIsOpen(!isOpen);
    };

    const handleClose = () => {
        setShow(false);
    };

    const handleShow = () => setShow(true);

    const handleRangeChange = (event) => {
        if (loading) return;
        setDayIndex(+event.target.value);
    };

    const handleRangeChangeEnd = (event) => {
        if (loading) return;
        const newDayIndex = +event.target.value;
        setDayIndex(newDayIndex);
        handleBasinSelect(selectedBasinId, exceededDataView, newDayIndex);
    };

    const exceededDataViewChange = (event) => {
        if (loading) return;
        const newValue = event.target.value;
        setExceededDataView(newValue);
        if (selectedTab === 'ed') {
            handleBasinSelect(selectedBasinId, newValue, dayIndex);
        }
    };

    const handleTabClick = (currentTab) => {
        if (loading) return;
        if (currentTab !== activeTab) {
            setActiveTab(+currentTab);
            if (currentTab === 4 || currentTab === 5) {
                setCurrentTab(null);
            } else {
                setCurrentTab('layer');
            }
        }
    };

    const handleResize = () => {
        setHeight(window.innerHeight);
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const fetchBasins = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL_P3}/lookups/fews_basins`);
                if (!response.ok) {
                    throw new Error(`Error: ${response.status}`);
                }
                const result = await response.json();
                return { basins: result.data || [], allBasinIds: result.data[result.data.length - 1].basin_id };
            } catch (error) {
                console.error('Failed to fetch basins:', error);
                throw error;
            }
        };

        const fetchData = async () => {
            try {
                setLookUpLoading(true);
                const [basinsData] = await Promise.all([fetchBasins()]);
                setBasins(basinsData.basins);
                setSelectedBasinIds(basinsData.allBasinIds);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLookUpLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (selectedBasinId) {
            handleBasinSelect(selectedBasinId, exceededDataView, dayIndex);
        }
    }, [selectedBasinId]);

    useEffect(() => {
        if (selectedTab) {
            handleApply();
        }
    }, [selectedTab]);

    const handleApply = () => {
        if (selectedTab === 'cf') {
            setStationDataType('forecast');
        } else if (selectedTab === 'hf') {
            setStationDataType('historic');
        } else if (selectedTab === 'ed') {
            setStationDataType('exceed');
        }
        handleBasinSelect(selectedBasinId, exceededDataView, dayIndex);
    };

    const handleCheckboxChange = (event) => {
        const basinId = event.target.value;
        setSelectedBasinIds(+basinId);
    };

    const handleBasinSelect = async (selectedIds, exceededDataViewParam, dayIndexParam) => {
        setSelectedBasinMap(null);
        setLoading(prev => prev + 1);

        let url, payload;
        if (selectedTab === 'ed') {
            payload = {
                purpose: exceededDataViewParam,
                basin_id: selectedIds,
                day_index: dayIndexParam,
            };
            url = '/fews/basin_rp';
        } else {
            if (selectedIds.length === 0) {
                setLoading(prev => (prev > 0 ? prev - 1 : 0));
                return;
            }
            payload = {
                basin_id: selectedIds,
                river_raster: false,
            };
            url = '/fews/basin_map';
        }
        try {
            const response = await fetch(`${envConfig.API_URL_P3}${url}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }

            const result = await response.json();
            setMapGeojsonLegend(result.data);
            setSelectedBasinMap(result.data);
        } catch (error) {
            console.error('Failed to send basin:', error);
        } finally {
            setLoading(prev => (prev > 0 ? prev - 1 : 0));
        }
    };

    const getMapData = async (result_data_geojson, colorPallet) => {
        let geojson = result_data_geojson;
        let data = {
            geojson: [],
            legend: [],
        };

        let unique_total = [...new Set(geojson.features.map(a => (isTemporal ? a.properties.temporal?.[selectedYear] : a.properties.total).filter(Boolean)))].sort((a, b) => a - b);

        const maxTotal = Math.max(...unique_total) ?? 5;
        const minTotal = 1;
        const validMaxTotal = isFinite(maxTotal) ? maxTotal : 5;
        const rangeSize = Math.ceil(validMaxTotal / 5);

        let ranges = [];
        let currentRange = [];
        let currentRangeEnd = minTotal + rangeSize - 1;

        unique_total.forEach((value) => {
            if (value <= currentRangeEnd) {
                currentRange.push(value);
            } else {
                ranges.push(currentRange);
                currentRange = [value];
                currentRangeEnd += rangeSize;
            }
        });

        if (currentRange.length > 0) {
            ranges.push(currentRange);
        }

        const totalToColorMap = {};
        ranges.forEach((range, index) => {
            range.forEach((value) => {
                totalToColorMap[value] = colorPallet ? colorPallet[index]?.color : null;
            });
        });

        geojson.features.forEach((feature) => {
            const total = isTemporal ? feature.properties.temporal?.[selectedYear] : feature.properties.total;
            feature.properties.color = totalToColorMap[total];
        });

        data.geojson = geojson;
        data.legend = colorPallet;

        return data;
    };

    useEffect(() => {
        const updateBasinMap = async () => {
            if (!mapGeojsonLegend) return;
            try {
                const data = await getMapData(mapGeojsonLegend.geojson, mapGeojsonLegend.color_scale);
                setSelectedBasinMap(data);
            } catch (error) {
                console.error('Failed to update basin map:', error);
            }
        };

        if (isTemporal) {
            updateBasinMap();
        }
    }, [selectedYear]);

    const LookUpLoader = () => (
        <div>
            <div className="spinner-border text-primary" role="status" style={{ width: '1rem', height: '1rem' }}>
                <span className="sr-only"></span>
            </div>
        </div>
    );

    useEffect(() => {
        const dropdownHeading = document.getElementsByClassName('dropdown-heading-value');
        if (dropdownHeading.length > 0) {
            if (selectedCountries.length === countries.length) {
                dropdownHeading[0].textContent = 'All selected';
            } else if (selectedCountries.length > 0) {
                dropdownHeading[0].textContent = `${selectedCountries.length} selected`;
            } else {
                dropdownHeading[0].textContent = 'Select Countries';
            }
        }
    }, [selectedCountries]);

    return (
        <>
            <nav className="navbar navbar-expand-lg navbar-light bg-light fixed-top p-1">
                <div className="container-fluid px-0">
                    <Link className="navbar-brand" to="/">
                        <img loading="lazy" src="/assets/images/logo.svg" alt="Logo" />
                    </Link>
                    <button
                        className="navbar-toggler"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#navbarText"
                        aria-controls="navbarText"
                        aria-expanded="false"
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>
                    <div className="collapse navbar-collapse pl-52px d-flex justify-content-between align-items-center" id="navbarText">
                        <div className="d-flex justify-content-center flex-grow-1">
                            <div className="d-flex align-items-center">
                                <h5 style={{ margin: "0" }}>{({
                                    'product1': 'Multi Hazard Mapping',
                                    'product2': 'Vulnerability and Risk Analysis',
                                    'product3': 'Flood Forecasting and Early Warning',
                                    'product4': 'Flood and Drought Monitor'
                                })[selectedProduct] || 'Flood and Drought Monitor'}</h5>
                                <Link
                                    to="#"
                                    onClick={() => { handleShow(); }}
                                    className={activeTab === 2 ? 'btn btn-contact me-1' : 'nav-link ms-3'}
                                    style={activeTab === 2 ? { width: '60px' } : {}}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                    title="Switch Product"
                                >
                                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M3 7H19M19 7L15 3M19 7L15 11M19 15H3M3 15L7 11M3 15L7 19"
                                            stroke={activeTab === 2 ? 'white' : 'black'}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                        <ul className="navbar-nav d-flex flex-row align-items-center">
                            <li className="nav-item me-2">
                                <Link
                                    to="#"
                                    className={activeTab === 1 ? 'btn btn-contact me-1' : 'nav-link'}
                                    style={activeTab === 1 ? { width: '60px' } : {}}
                                    onClick={() => setActiveTab(1)}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                    title="Layers"
                                >
                                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M1.8335 15.5833L11.0002 20.1667L20.1668 15.5833M1.8335 11L11.0002 15.5833L20.1668 11M11.0002 1.83334L1.8335 6.41668L11.0002 11L20.1668 6.41668L11.0002 1.83334Z"
                                            stroke={activeTab === 1 ? 'white' : 'black'}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Link>
                            </li>
                            <li className="nav-item me-2">
                                <Link
                                    to="#"
                                    className={activeTab === 4 ? 'btn btn-contact me-1' : 'nav-link'}
                                    style={activeTab === 4 ? { width: '60px' } : {}}
                                    onClick={() => !loading && setActiveTab(4)}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                    title="Help/Info"
                                >
                                    <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M8.83266 8.25001C9.04817 7.63737 9.47355 7.12077 10.0335 6.79172C10.5934 6.46266 11.2517 6.34237 11.8917 6.45216C12.5318 6.56196 13.1124 6.89474 13.5306 7.39158C13.9489 7.88841 14.1778 8.51724 14.1768 9.16668C14.1768 11 11.4268 11.9167 11.4268 11.9167M11.5002 15.5833H11.5093M20.6668 11C20.6668 16.0626 16.5628 20.1667 11.5002 20.1667C6.43755 20.1667 2.3335 16.0626 2.3335 11C2.3335 5.9374 6.43755 1.83334 11.5002 1.83334C16.5628 1.83334 20.6668 5.9374 20.6668 11Z"
                                            stroke={activeTab === 4 ? 'white' : 'black'}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Link>
                            </li>
                            <li className="nav-item me-2">
                                <Link
                                    to="#"
                                    className={activeTab === 5 ? 'btn btn-contact me-1' : 'nav-link'}
                                    style={activeTab === 5 ? { width: '60px' } : {}}
                                    onClick={() => !loading && setActiveTab(5)}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                    title="Export/Download"
                                >
                                    <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M19.75 13.75V17.4167C19.75 17.9029 19.5568 18.3692 19.213 18.713C18.8692 19.0568 18.4029 19.25 17.9167 19.25H5.08333C4.5971 19.25 4.13079 19.0568 3.78697 18.713C3.44315 18.3692 3.25 17.9029 3.25 17.4167V13.75M6.91667 9.16667L11.5 13.75M11.5 13.75L16.0833 9.16667M11.5 13.75V2.75"
                                            stroke={activeTab === 5 ? 'white' : 'black'}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Link>
                            </li>
                            <li>
                                <Link
                                    className="btn btn-contact me-1"
                                    to="/"
                                    style={{ width: '60px' }}
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="bottom"
                                    title="Home"
                                >
                                    <i className="bi bi-house fs-6"></i>
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>

            <div className="tab-content" id="myTabContent" style={{ height: '100%' }}>
                <div
                    className="help-info-container"
                    style={{ display: activeTab === 4 ? 'block' : 'none' }}
                >
                    <DashboardHelpInfoTab key="help-info" />
                </div>
                <div
                    className="export-download-container"
                    style={{ display: activeTab === 5 ? 'block' : 'none' }}
                >
                    <DashboardExportTab key="export-download" />
                </div>
                <div
                    className={`tab-pane ${currentTab === 'layer' && activeTab === 1 ? 'show active' : ''}`}
                    id="layer"
                    role="tabpanel"
                    aria-labelledby="layer-tab"
                    style={{ display: activeTab === 1 && currentTab === 'layer' ? 'block' : 'none' }}
                >
                    <section className="pt-65px">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-sm-12 col-md-12 col-lg-12 px-0">
                                    <div>
                                        <ul className="nav nav-tabs p3dashboard nav-justified" id="myTab" role="tablist" style={{ marginTop: '0px' }}>
                                            <li className="nav-item" role="presentation">
                                                <div className="px-2">
                                                    <div className="form-group px-2 mb-0 text-start">
                                                        <label className="mb-1 textLabel">
                                                            <span className="pe-2">
                                                                <img src="/assets/images/basin.svg" alt="" />
                                                            </span>
                                                            River Basins
                                                        </label>
                                                        {lookUpLoading || loading ? (
                                                            <LookUpLoader />
                                                        ) : (
                                                            <select
                                                                className="form-select formText"
                                                                value={selectedBasinId}
                                                                onChange={handleCheckboxChange}
                                                            >
                                                                {basins.map((basin) => (
                                                                    <option key={basin.basin_id} value={basin.basin_id}>
                                                                        {basin.basin}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </div>
                                                </div>
                                                {['ed'].includes(selectedTab) && !lookUpLoading && (
                                                    <div className={`sidenav p3 ${isOpen ? 'open' : 'collapsed'}`}>
                                                        <button className="toggle-btn newBtn" onClick={toggleSidenav}>
                                                            <img
                                                                src={isOpen ? '../assets/images/Layout.png' : '../assets/images/LayoutWhite.svg'}
                                                                alt="Toggle Sidenav"
                                                                className="toggle-icon"
                                                            />
                                                        </button>
                                                        {!lookUpLoading ? (
                                                            <>
                                                                {selectedTab === 'ed' ? (
                                                                    <div className="border-0" style={{ marginTop: '-34px', height: 'auto', overflowY: 'auto', overflowX: 'hidden' }}>
                                                                        <div className="card-body p-0 text-start">
                                                                            <div className="form-group">
                                                                                <div className="form-check form-check-inline">
                                                                                    <input
                                                                                        className="form-check-input"
                                                                                        type="radio"
                                                                                        name="inlineRadioOptions"
                                                                                        id="inlineRadio1"
                                                                                        value="rain"
                                                                                        onChange={exceededDataViewChange}
                                                                                        checked={exceededDataView === 'rain'}
                                                                                    />
                                                                                    <label className="form-check-label f-13px" htmlFor="inlineRadio1">
                                                                                        Accumulated rain
                                                                                    </label>
                                                                                </div>
                                                                                <div className="form-check form-check-inline">
                                                                                    <input
                                                                                        className="form-check-input"
                                                                                        type="radio"
                                                                                        name="inlineRadioOptions"
                                                                                        id="inlineRadio2"
                                                                                        value="flow"
                                                                                        onChange={exceededDataViewChange}
                                                                                        checked={exceededDataView === 'flow'}
                                                                                    />
                                                                                    <label className="form-check-label f-13px" htmlFor="inlineRadio2">
                                                                                        Daily flow
                                                                                    </label>
                                                                                </div>
                                                                                <div className="mt-2 w-100" style={{ position: 'relative' }}>
                                                                                    <label className="form-check-label f-13px">Lead time (days)</label>
                                                                                    <input
                                                                                        type="range"
                                                                                        min="0"
                                                                                        max="9"
                                                                                        step="1"
                                                                                        value={dayIndex}
                                                                                        onChange={handleRangeChange}
                                                                                        onMouseUp={(e) => handleRangeChangeEnd(e)}
                                                                                        onTouchEnd={(e) => handleRangeChangeEnd(e)}
                                                                                        style={{
                                                                                            width: '100%',
                                                                                            margin: 0,
                                                                                            padding: 0,
                                                                                            boxSizing: 'border-box',
                                                                                            background: 'transparent',
                                                                                        }}
                                                                                    />
                                                                                    <div
                                                                                        style={{
                                                                                            position: 'relative',
                                                                                            width: '100%',
                                                                                            display: 'flex',
                                                                                            justifyContent: 'space-between',
                                                                                            fontSize: '10px',
                                                                                            fontWeight: '500',
                                                                                            color: '#000',
                                                                                            marginTop: '4px',
                                                                                            padding: '0 9px 0 6px',
                                                                                            boxSizing: 'border-box',
                                                                                        }}
                                                                                    >
                                                                                        {[...Array(10)].map((_, i) => (
                                                                                            <span key={i} style={{ textAlign: 'center', width: '1px', transform: 'translateX(-50%)' }}>
                                                                                                {i + 1}
                                                                                            </span>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ) : ''}
                                                            </>
                                                        ) : ' '}
                                                    </div>
                                                )}
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button
                                                    className={`nav-link ${selectedTab === 'cf' ? 'active' : ''}`}
                                                    onClick={() => onTabSelect('cf')}
                                                    type="button"
                                                    role="tab"
                                                    aria-controls="cf"
                                                    aria-selected={selectedTab === 'cf'}
                                                >
                                                    Current Forecast
                                                </button>
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button
                                                    className={`nav-link ${selectedTab === 'hf' ? 'active' : ''}`}
                                                    onClick={() => onTabSelect('hf')}
                                                    type="button"
                                                    role="tab"
                                                    aria-controls="hf"
                                                    aria-selected={selectedTab === 'hf'}
                                                >
                                                    Historical Flooding
                                                </button>
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button
                                                    className={`nav-link ${selectedTab === 'ed' ? 'active' : ''}`}
                                                    onClick={() => onTabSelect('ed')}
                                                    type="button"
                                                    role="tab"
                                                    aria-controls="ed"
                                                    aria-selected={selectedTab === 'ed'}
                                                >
                                                    Flood Exceedance
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="col-sm-12 col-md-12 col-lg-12 px-0">
                                    <div className="tab-content" id="myTabContent">
                                        <div
                                            className={`tab-pane fade ${selectedTab === 'cf' ? 'show active' : ''}`}
                                            id="cf"
                                            role="tabpanel"
                                            aria-labelledby="cf-tab"
                                        ></div>
                                        <div
                                            className={`tab-pane fade ${selectedTab === 'hf' ? 'show active' : ''}`}
                                            id="hf"
                                            role="tabpanel"
                                            aria-labelledby="hf-tab"
                                        ></div>
                                        <div
                                            className={`tab-pane fade ${selectedTab === 'ed' ? 'show active' : ''}`}
                                            id="ed"
                                            role="tabpanel"
                                            aria-labelledby="ed-tab"
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-sm-12 col-md-12 col-lg-12 px-0">
                                <RiverMap
                                    selectedBasinMap={selectedBasinMap}
                                    dataType={stationDataType}
                                    selectedTab={selectedTab}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="App">
                <SwitchProducts show={show} handleClose={handleClose} />
            </div>
        </>
    );
}

export default Dashboard;